import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { AlertTriangle, Loader2, Send, Info, AlertCircle, Flame, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SEVERITY_STYLES = {
  info: { icon: Info, bg: 'from-sky-600 to-sky-700', accent: 'sky', label: 'Informativo' },
  aviso: { icon: AlertCircle, bg: 'from-amber-600 to-orange-600', accent: 'amber', label: 'Aviso' },
  critico: { icon: Flame, bg: 'from-rose-700 via-red-700 to-red-900', accent: 'rose', label: 'CRÍTICO' },
};

/**
 * AdminAlertBlockingPopup
 * Polls /api/admin-alerts/pending every 20s.
 * If there are pending alerts addressed to the user, blocks the entire screen
 * with a fullscreen red popup. User MUST type a response (>= 10 chars) to dismiss.
 * Cycles through multiple alerts one-by-one (oldest first).
 */
export const AdminAlertBlockingPopup = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [response, setResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchPending = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/admin-alerts/pending`);
      const all = Array.isArray(res.data) ? res.data : [];
      // Only alerts here — recognitions handled by RecognitionBlockingPopup
      setPending(all.filter(a => (a.kind || 'alert') === 'alert'));
    } catch {
      /* silent — could be expired token */
    }
  }, [user]);

  useEffect(() => {
    fetchPending();
    const t = setInterval(fetchPending, 20000);
    return () => clearInterval(t);
  }, [fetchPending]);

  // Reset response when active alert changes
  useEffect(() => { setResponse(''); }, [pending[0]?.id]);

  if (!user || pending.length === 0) return null;

  const current = pending[0];
  const sev = SEVERITY_STYLES[current.severity] || SEVERITY_STYLES.aviso;
  const Icon = sev.icon;

  const handleRespond = async () => {
    const r = response.trim();
    if (r.length < 10) {
      toast.error('A resposta deve ter pelo menos 10 caracteres');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/admin-alerts/${current.id}/respond`, { response: r });
      toast.success('Resposta enviada');
      setPending(prev => prev.filter(a => a.id !== current.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao responder');
    } finally {
      setSubmitting(false);
    }
  };

  const openLink = () => {
    if (current.contract_id) {
      navigate(`/contracts/${current.contract_id}`);
    } else if (current.lead_id) {
      navigate(`/leads/${current.lead_id}`);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(6px)',
      }}
      data-testid="admin-alert-blocking-popup"
    >
      <div className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white/20 animate-in zoom-in-95 duration-300">
        {/* Pulsing header */}
        <div className={`bg-gradient-to-r ${sev.bg} px-6 py-4 flex items-center gap-3`}>
          <div className="relative flex items-center justify-center">
            <span className={`animate-ping absolute inline-flex h-8 w-8 rounded-full bg-white/40 opacity-75`} />
            <Icon className="w-7 h-7 text-white relative" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-white/80 font-bold">Alerta · {sev.label}</p>
            <p className="text-base font-bold text-white truncate">De: {current.sender_name}</p>
          </div>
          {pending.length > 1 && (
            <span className="bg-white/20 text-white text-xs font-bold px-2.5 py-1 rounded-full">
              1 de {pending.length}
            </span>
          )}
        </div>

        {/* Body */}
        <div className="bg-white px-6 py-6 space-y-5">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-base text-slate-800 whitespace-pre-wrap leading-relaxed">{current.message}</p>
          </div>

          {(current.contract_id || current.lead_id) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openLink}
              className="gap-2 rounded-full"
              data-testid="alert-open-link-btn"
            >
              <ExternalLink className="w-4 h-4" />
              Abrir {current.contract_id ? 'contrato' : 'lead'} relacionado
            </Button>
          )}

          <div>
            <label className="text-sm font-semibold text-slate-700 block mb-2">
              A tua resposta * (mínimo 10 caracteres — obrigatória)
            </label>
            <Textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              rows={4}
              placeholder="O que vais fazer / o que respondes ao remetente..."
              className="resize-none"
              data-testid="alert-response-textarea"
              autoFocus
            />
            <p className={`text-xs mt-1 ${response.trim().length < 10 ? 'text-rose-600' : 'text-emerald-600'}`}>
              {response.trim().length}/10 caracteres
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              onClick={handleRespond}
              disabled={submitting || response.trim().length < 10}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 gap-2 h-12 text-base font-semibold"
              data-testid="alert-respond-submit-btn"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              Confirmar e Responder
            </Button>
          </div>

          <p className="text-xs text-center text-slate-400 pt-1">
            Este alerta foi enviado por <strong>{current.sender_name}</strong> e ficou registado na auditoria. Não é possível fechar sem responder.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminAlertBlockingPopup;
